/**
 * Starting a free trial and proving the email address. The verification
 * link's token is lifted out of the address bar on arrival (so it is not left
 * in history, and is never mistaken for a password-reset token), then redeemed.
 */

import { apiCall } from '../api.ts';

const VERIFY_PATH = '/verify-email';
const KEY = 'synanalytics_verify';

export interface SignUpInput {
  first_name: string;
  last_name: string;
  agency_name: string;
  email: string;
  password: string;
  turnstile_token: string;
}

export const startTrial = (input: SignUpInput) =>
  apiCall<{ organisation: { id: string; name: string; trial_ends_at: string } }>('/auth/signup', {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const verifyEmail = (token: string) =>
  apiCall<{ verified: boolean }>('/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });

export const resendVerification = () =>
  apiCall<{ sent: boolean }>('/auth/verify-email/resend', { method: 'POST', body: '{}' });

export const accountState = () => apiCall<{ mfa_enabled: boolean; email_verified: boolean }>('/me');

/** Takes the token from a verification link, if this page load is one, and cleans the URL. */
export function captureVerifyFromUrl(): void {
  if (!window.location.pathname.startsWith(VERIFY_PATH)) return;
  const token = new URLSearchParams(window.location.search).get('token');
  window.history.replaceState({}, '', '/');
  if (!token) return;
  try {
    sessionStorage.setItem(KEY, token);
  } catch {
    // Storage blocked: the link can be opened again from the email.
  }
}

/** The pending verification token, removed as it is read: it can only be spent once. */
export function takeVerifyToken(): string | null {
  try {
    const token = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    return token;
  } catch {
    return null;
  }
}
